import React, { useEffect, useMemo, useState } from "react";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import { benchmarkRequestApis } from "../../api/benchmarkRequestApis";
import PeerExtractedTable from "./PeerExtractedTable";

function reportIdOf(req) {
  const id = req?.report ?? req?.reportId;
  if (!id) return "";
  if (typeof id === "object") return String(id._id || id.id || "");
  return String(id);
}

/**
 * Shows the latest completed peer extract for this report on the main
 * Industry Avg (etc.) dashboard. CTA to Request Peers when none exist.
 */
export default function PeerExtractPanel({ reportId, onOpenRequestPeers }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    benchmarkRequestApis
      .listMine()
      .then((rows) => {
        if (!cancelled) setRequests(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setRequests([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reportId]);

  const latest = useMemo(() => {
    if (!reportId) return null;
    const rid = String(reportId);
    const matches = requests
      .filter(
        (r) =>
          r.status === "completed" &&
          Array.isArray(r.peerExtracted) &&
          r.peerExtracted.length > 0 &&
          reportIdOf(r) === rid,
      )
      .sort(
        (a, b) =>
          new Date(b.completedAt || b.updatedAt || b.createdAt || 0) -
          new Date(a.completedAt || a.updatedAt || a.createdAt || 0),
      );
    return matches[0] || null;
  }, [requests, reportId]);

  if (loading) {
    return (
      <section className="peer-extract-panel" aria-busy="true" aria-label="Peer extract results">
        <header className="peer-extract-panel__header">
          <div>
            <p className="peer-extract-panel__eyebrow">Automation</p>
            <h2 className="peer-extract-panel__title">Peer extract results</h2>
          </div>
        </header>
        <div className="peer-extract-panel__loading">
          <HourglassEmptyOutlinedIcon fontSize="small" />
          <span>Checking completed peer extracts…</span>
        </div>
      </section>
    );
  }

  if (!latest) {
    return (
      <section className="peer-extract-panel peer-extract-panel--empty" aria-label="Peer extract results">
        <header className="peer-extract-panel__header">
          <div>
            <p className="peer-extract-panel__eyebrow">Automation</p>
            <h2 className="peer-extract-panel__title">Peer extract results</h2>
          </div>
        </header>

        <div className="peer-extract-empty">
          <div className="peer-extract-empty__icon" aria-hidden="true">
            <GroupsOutlinedIcon />
          </div>
          <div className="peer-extract-empty__copy">
            <h3 className="peer-extract-empty__heading">No peer extracts yet</h3>
            <p className="peer-extract-empty__text">
              Request Peers pulls ESG datapoints for selected companies and lists
              them here — headcount, production, waste, water, emissions, and more.
            </p>
          </div>
          {typeof onOpenRequestPeers === "function" ? (
            <button
              type="button"
              className="peer-extract-empty__cta"
              onClick={onOpenRequestPeers}
            >
              <GroupsOutlinedIcon fontSize="small" />
              <span className="peer-extract-empty__cta-label">Request Peers</span>
              <ArrowForwardRoundedIcon
                className="peer-extract-empty__cta-arrow"
                fontSize="small"
              />
            </button>
          ) : null}
        </div>
      </section>
    );
  }

  const peers = (latest.companies || []).map((c) => c.name).filter(Boolean);
  return (
    <section className="peer-extract-panel" aria-label="Peer extract results">
      <header className="peer-extract-panel__header">
        <div>
          <p className="peer-extract-panel__eyebrow">Automation</p>
          <h2 className="peer-extract-panel__title">Peer extract results</h2>
        </div>
        <span className="peer-extract-panel__badge">
          <CheckCircleOutlineRoundedIcon fontSize="inherit" />
          Completed
        </span>
      </header>

      <p className="peer-extract-panel__meta">
        Latest request
        {peers.length ? ` · ${peers.join(", ")}` : ""}
        {latest.completedAt
          ? ` · ${new Date(latest.completedAt).toLocaleString()}`
          : ""}
      </p>

      <PeerExtractedTable
        rows={latest.peerExtracted}
        title={`Extracted datapoints (${latest.peerExtracted.length})`}
      />
    </section>
  );
}
